import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';

import { Sample } from '../../interfaces/sample.interface';
import { SampleActions } from '../actions/sample.actions';

export const sampleFeatureKey = 'sample';

export interface SampleState extends EntityState<Sample> {
  error: unknown;
  pending: boolean;
}

export const adapter: EntityAdapter<Sample> = createEntityAdapter<Sample>({
  selectId: (sample): string => `${sample.orderId}-${sample.id}`,
});

export const initialState: SampleState = adapter.getInitialState({
  error: null,
  pending: false,
});

export const reducer = createReducer(
  initialState,
  on(SampleActions.completeSample, (state) => {
    return {
      ...state,
      error: null,
      pending: true,
    };
  }),
  on(
    SampleActions.completeSampleSuccess,
    (state, { sample }): SampleState =>
      adapter.upsertOne(sample, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(SampleActions.completeSampleFailure, (state, { error }) => {
    return {
      ...state,
      error,
      pending: false,
    };
  }),
  on(SampleActions.createSample, (state) => {
    return {
      ...state,
      error: null,
      pending: true,
    };
  }),
  on(
    SampleActions.createSampleSuccess,
    (state, { sample }): SampleState =>
      adapter.upsertOne(sample, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(SampleActions.createSampleFailure, (state, { error }) => {
    return {
      ...state,
      error,
      pending: false,
    };
  }),
  on(SampleActions.updateSample, (state) => {
    return {
      ...state,
      error: null,
      pending: true,
    };
  }),
  on(
    SampleActions.updateSampleSuccess,
    (state, { sample }): SampleState =>
      adapter.upsertOne(sample, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(SampleActions.updateSampleFailure, (state, { error }) => {
    return {
      ...state,
      error,
      pending: false,
    };
  }),
  on(SampleActions.loadSample, (state) => {
    return {
      ...state,
      error: null,
      pending: true,
    };
  }),
  on(
    SampleActions.loadSampleSuccess,
    (state, { sample }): SampleState =>
      adapter.upsertOne(sample, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(SampleActions.loadSampleFailure, (state, { error }) => {
    return {
      ...state,
      error,
      pending: false,
    };
  }),
  on(SampleActions.loadSamples, (state) => {
    return {
      ...state,
      error: null,
      pending: true,
    };
  }),
  on(
    SampleActions.loadSamplesSuccess,
    (state, { samples }): SampleState =>
      adapter.upsertMany(samples, {
        ...state,
        error: null,
        pending: false,
      }),
  ),
  on(SampleActions.loadSamplesFailure, (state, { error }) => {
    return {
      ...state,
      error,
      pending: false,
    };
  }),
  on(SampleActions.adminLoadSamples, (state) => {
    return {
      ...state,
      error: null,
      pending: true,
    };
  }),
  on(SampleActions.clearSamples, (state) => adapter.removeAll(state)),
);

export const sampleFeature = createFeature({
  name: sampleFeatureKey,
  reducer,
  extraSelectors: ({ selectSampleState }) => ({
    ...adapter.getSelectors(selectSampleState),
  }),
});

export const { selectIds, selectEntities, selectAll, selectTotal } =
  sampleFeature;
